
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function NexusResearchReview({ reload }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("active");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
const [detailLoading, setDetailLoading] = useState(false);
const [detailError, setDetailError] = useState("");

async function openResearch(recordId) {
  setSelected(null);
  setDetailError("");
  setDetailLoading(true);

  try {
    const response = await api.get(
      `/ai-worker/research/${recordId}`
    );
    setSelected(response.data);
  } catch (err) {
    setDetailError(
      formatApiErrorDetail(
        err,
        t("nexusResearch.detailFailed", "Unable to load research details")
      )
    );
  } finally {
    setDetailLoading(false);
  }
}

  useEffect(() => {
    let active = true;

    async function loadResearch() {
      setLoading(true);
      setError("");

      try {
        const response = await api.get(tab === "archived" ? "/ai-worker/archive" : "/ai-worker/queue", {
          params: { limit: 25 },
        });

        if (active) {
          setRecords(response.data.items || []);
        }
      } catch (err) {
        if (active) {
          setError(
            formatApiErrorDetail(
              err,
              t("nexusResearch.loadFailed", "Unable to load research")
            )
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    loadResearch();

    return () => {
      active = false;
    };
  }, [reload, t, tab]);

  return (
    <section className="rounded-2xl border border-[#C5A059]/40 bg-[#F8F7F4] p-5 shadow-lg space-y-4">
      <h2 className="rounded-xl bg-[#0B2545] px-5 py-5 text-xl font-semibold tracking-wide text-[#C5A059] shadow-sm">
        {t("nexusResearch.title", "NEXUS Research Review Desk")}
      </h2>

      {loading && (
        <p role="status">
          {t("nexusResearch.loading", "Loading research...")}
        </p>
      )}

      {error && <p role="alert" className="text-red-700">{error}</p>}

      {!loading && !error && records.length === 0 && (
        <p className="text-sm text-slate-600">
          {t("nexusResearch.empty", "No research records available.")}
        </p>
      )}

      {!loading && !error && records.length > 0 && (
        <ul className="divide-y divide-slate-200">
          {records.map((record) => (
            <li key={record.id} className="py-3">
             <button
  type="button"
  onClick={() => openResearch(record.id)}
  className="font-medium text-left text-blue-700 hover:underline"
>
  {record.title || record.id}
</button>
              <p className="text-sm text-slate-600">
                {t("nexusResearch.status", "Status")}: {record.status}
              </p>
              <p className="text-sm text-slate-600">
                {t("nexusResearch.verification", "Verification")}:{" "}
                {record.verification_status || "unverified"}
              </p>
            </li>
          ))}
        </ul>
      )}
      {detailLoading && <p role="status">{t("nexusResearch.detailLoading", "Loading research details...")}</p>}
      {detailError && <p role="alert" className="text-red-700">{detailError}</p>}
      {selected && (
        <div className="border-t border-slate-200 pt-4 space-y-2">
          <h3 className="font-semibold text-[#0B2545]">
            {t("nexusResearch.details", "Research Details")}
          </h3>
          <p className="text-sm">
            {selected.title || selected.id}
          </p>
          <p className="text-sm text-slate-600">
            {t("nexusResearch.status", "Status")}: {selected.status}
          </p>
          <p className="text-sm text-slate-600">
            {t("nexusResearch.verification", "Verification")}:{" "}
            {selected.verification_status}
          </p>
        </div>
      )}
    </section>
  );
}
